import { AgChartOptions } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import { getTotalTime } from 'business/shift-report/report/pages/ReportAggregated/services';
import { TopLevelTasksAggregate } from 'business/shift-report/report/pages/ReportAggregated/types';
import { formatDurationToReadable } from 'business/shift-report/report/services/timeOperations';
import { createNumberFormatter } from 'technical/format/number';
import { toPercent } from 'technical/utils/converter';

interface AggregatedTaskPieChartProps {
  aggregatedTasks: TopLevelTasksAggregate[];
  criticalTime: boolean;
  isMobile?: boolean;
}

const formatLegend = (
  datum: any,
  totalMinutes: number,
  criticalTime: boolean,
  formatter: Intl.NumberFormat,
) => {
  const totalValue = criticalTime ? (datum.criticalTotal ?? 0) : datum.total;
  return `${datum.name} - ${formatter.format(toPercent(totalValue / (totalMinutes === 0 ? 1 : totalMinutes)))}%`;
};

const formatTooltip = (datum: any, criticalTime: boolean) => {
  const value = criticalTime ? (datum.criticalTotal ?? 0) : datum.total;
  const duration = dayjs.duration(value, 'minutes');
  return formatDurationToReadable(duration.asMinutes());
};

export const AggregatedTaskPieChart = ({
  aggregatedTasks,
  criticalTime = false,
  isMobile = false,
}: AggregatedTaskPieChartProps) => {
  const { language } = useTranslation();
  const totalMinutes = getTotalTime(aggregatedTasks, criticalTime);

  const numberFormatter = createNumberFormatter(language);

  const chartOptions = {
    data: aggregatedTasks,
    series: [
      {
        type: 'pie',
        angleKey: criticalTime ? 'criticalTotal' : 'total',
        legendItemKey: 'name',
        tooltip: {
          renderer: ({ datum }) => {
            return {
              data: [
                {
                  label: datum.name,
                  value: formatTooltip(datum, criticalTime),
                },
              ],
            };
          },
        },
      },
    ],
    minHeight: 400,
    minWidth: 0,
    legend: {
      enabled: true,
      position: isMobile ? 'bottom' : 'right',
      item: {
        label: {
          formatter: ({ datum }) => {
            return formatLegend(
              datum,
              totalMinutes,
              criticalTime,
              numberFormatter,
            );
          },
        },
      },
    },
    animation: {
      enabled: false,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
  } satisfies AgChartOptions;

  return <AgCharts options={chartOptions} />;
};
